import { useCallback, useEffect, useMemo, useState } from "react";

import capitalize from "lodash/capitalize";

import { Table } from "@similarweb/pro-logic-components";
import { Button, IconButton } from "@similarweb/ui-components";
import { Stack, Flex } from "@similarweb/ui-components/foundations";

import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/numberFilter";
import { EDataType } from "app/pages/digital-marketing/ad-intelligence/advertisers/overview/components/types";
import { useTableRows } from "app/pages/digital-marketing/ad-intelligence/common/advertisersPublishers/hooks/useTableRows";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import { TableBlur } from "app/pages/digital-marketing/ad-intelligence/common/shared/TableBlur";
import { allTrackers } from "app/services/track/track";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { entityTypeMap } from "pro-features/components/AutocompleteAdIntelligence/AutocompleteAdIntelligenceHelper";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { publisherAnalysisNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisNavigator";

import { TableFooter } from "../overview/StyledComponents";
import { switcherToDataIndexMap } from "../shared/hooks/useSwitcherLogic";
import { ShareTypeSwitcher, SwitcherOption } from "../shared/ShareTypeSwitcher";
import { HookOverlayContent } from "../shared/TableComponents";
import type { AdIntelligenceController } from "../shared/types/types";
import { WidgetBoxWithMarginBottom } from "../StyledComponents";
import { usePaddedPreviewData } from "../utils/padPreviewData";
import type { ISortProps } from "../utils/utils";
import {
    getNoDataObject,
    hasAdIntelligenceOverviewAccess,
    isAdIntelligenceNoTouchPreviewVariant,
    openAdIntelligenceHookModal,
    PAGE_SIZE_SMALL,
} from "../utils/utils";

import { getColumns } from "./getTableTopColumns";
import { getCompareColumns } from "./getTableTopCompareColumns";

export const TABLE_ID = "top-advertisers-publishers-table";

interface ITopAdvertisersPublishersFooterProps {
    isPreview: boolean;
    totalCount?: number;
    controller: AdIntelligenceController;
    resource: AdIntelligenceController;
    onSeeAll: () => void;
}

const TopAdvertisersPublishersFooter = ({
    isPreview,
    totalCount,
    controller,
    resource,
    onSeeAll,
}: ITopAdvertisersPublishersFooterProps) => {
    if (isPreview) {
        return (
            <TableFooter>
                <Button
                    type="flat"
                    onClick={onSeeAll}
                >
                    {translate("ad.intelligence.overview.see.spend.impressions.button")}
                </Button>
            </TableFooter>
        );
    }

    if (!totalCount) {
        return null;
    }

    return (
        <TableFooter>
            <IconButton
                type="flat"
                iconName="arrow-right"
                placement="right"
                onClick={onSeeAll}
            >
                {translate(`ad.intelligence.${controller}.${resource}s.table.see.all.button`, {
                    totalCount: numberFilter()(totalCount),
                })}
            </IconButton>
        </TableFooter>
    );
};

interface ITopAdvertisersPublishersTableProps {
    isPreview?: boolean;
    controller?: AdIntelligenceController;
    resource?: AdIntelligenceController;
}

export const TopAdvertisersPublishersTable = ({
    isPreview = false,
    controller = "advertiser",
    resource = "publisher",
}: ITopAdvertisersPublishersTableProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const hasAdIntelBeta = hasAdIntelligenceOverviewAccess(isPreview);
    const isNoTouchVariant = isAdIntelligenceNoTouchPreviewVariant(isPreview);
    const isOpenPreview = isPreview && hasAdIntelBeta;
    const sortBy = hasAdIntelBeta && !isPreview ? EDataType.SPEND : EDataType.VISITS;
    const isCompareMode = params.key.split(",").length > 1;
    const [selectedSwitcherOption, setSelectedSwitcherOption] = useState<SwitcherOption>(
        isPreview ? SwitcherOption.Visits : SwitcherOption.Spend,
    );

    const { data, isLoading, isError } = useTableRows({
        controller,
        resource,
        page: 1,
        pageSize: PAGE_SIZE_SMALL,
        isCompareMode,
        sort: isCompareMode ? ({} as unknown as ISortProps) : { sort: sortBy, asc: false },
        withChildren: false,
    });

    useEffect(() => {
        if (data?.hasAdIntelMetrics === false) {
            setSelectedSwitcherOption(SwitcherOption.Visits);
        }
    }, [data?.hasAdIntelMetrics]);

    const handleSeeAll = useCallback(() => {
        if (hasAdIntelBeta) {
            allTrackers.dispatchTrackEvent(
                "Internal Link",
                "click",
                `${capitalize(controller)}_overview/Top_${resource}s/see_more`,
            );
            if (controller === entityTypeMap.advertiser) {
                void displayAdvertisingNavigation.publishers.go({
                    ...params,
                    webSource: devicesTypes.TOTAL,
                });
            } else {
                void publisherAnalysisNavigation.advertisers.go({
                    ...params,
                    webSource: devicesTypes.DESKTOP,
                });
            }
        } else {
            openAdIntelligenceHookModal(controller);
        }
    }, [hasAdIntelBeta, params, controller, resource]);

    const paddedData = usePaddedPreviewData(
        data?.items,
        PAGE_SIZE_SMALL,
        isPreview && !isCompareMode && data?.totalCount > 0,
    );

    const columns = useMemo(() => {
        if (data?.hasAdIntelMetrics === undefined) {
            return [];
        }

        if (isCompareMode) {
            return getCompareColumns(
                params.pdf,
                isPreview,
                resource,
                selectedSwitcherOption,
                handleSeeAll,
                data?.items?.length,
                hasAdIntelBeta,
            );
        }

        return getColumns(
            params.pdf,
            data.hasAdIntelMetrics,
            isPreview,
            handleSeeAll,
            data?.items?.length,
            resource,
            hasAdIntelBeta,
        );
    }, [
        data?.hasAdIntelMetrics,
        data?.items?.length,
        params.pdf,
        isPreview,
        handleSeeAll,
        resource,
        isCompareMode,
        selectedSwitcherOption,
        hasAdIntelBeta,
    ]);

    const noDataObject = getNoDataObject(isError, controller);

    const isHooked = isPreview && !hasAdIntelBeta;

    const blurColumn = isCompareMode ? switcherToDataIndexMap[selectedSwitcherOption] : "spend";
    const showBlurOverlay = isHooked && (!isCompareMode || selectedSwitcherOption !== SwitcherOption.Visits);

    return (
        <WidgetBoxWithMarginBottom id={TABLE_ID}>
            <TableBlur
                startColumn={blurColumn}
                endColumn={isCompareMode ? blurColumn : "impressions"}
                enabled={showBlurOverlay}
                isLoading={isLoading}
                blurContent={
                    <HookOverlayContent
                        title={translate(`ad.intelligence.overview.${resource}s.table.preview.cell.title`)}
                        subtitle={translate(`ad.intelligence.overview.${resource}s.table.preview.cell.subtitle`)}
                        buttonText={translate(`ad.intelligence.overview.${resource}s.table.preview.cell.button`)}
                        isNoTouchVariant={isNoTouchVariant}
                        onButtonClick={() => openAdIntelligenceHookModal(controller)}
                        dataAutomation={`hook-overlay-top-${resource}s-table`}
                    />
                }
            >
                <Stack>
                    <Table
                        key={`${data?.hasAdIntelMetrics?.toString()}-${selectedSwitcherOption}`}
                        theme="white"
                        bordersConfig={{ body: false, header: false }}
                        columnConfig={columns}
                        data={paddedData}
                        isLoading={isLoading}
                        loaderRowCount={5}
                        noData={noDataObject}
                        withArrowButtons={!!data?.totalCount}
                    >
                        <Table.TableTop withoutRowBorders>
                            <Table.TableTop.Title
                                title={translate(`ad.intelligence.${controller}.top.${resource}s.table.title`)}
                                tooltip={translate(
                                    `ad.intelligence.${controller}.top.${resource}s.table.title.tooltip`,
                                    {
                                        sortBy: data?.hasAdIntelMetrics ? sortBy : "visits",
                                    },
                                )}
                                filters={subtitleFilters}
                            />
                            {isCompareMode && data?.items?.length > 0 && !isOpenPreview ? (
                                <Flex margin="0 0 12px 24px">
                                    <ShareTypeSwitcher
                                        selectedSwitcherOption={selectedSwitcherOption}
                                        setSelectedSwitcherOption={setSelectedSwitcherOption}
                                    />
                                </Flex>
                            ) : null}
                        </Table.TableTop>
                    </Table>
                </Stack>
            </TableBlur>
            {!isHooked ? (
                <TopAdvertisersPublishersFooter
                    isPreview={isPreview}
                    totalCount={data?.totalCount}
                    controller={controller}
                    resource={resource}
                    onSeeAll={handleSeeAll}
                />
            ) : null}
        </WidgetBoxWithMarginBottom>
    );
};
